import React from 'react';
import PropTypes from 'prop-types';
import Sparkline from '../Sparkline/Sparkline';
import './StatCard.css';

const DELTA_ICONS = {
	up: 'bi bi-arrow-up-short',
	down: 'bi bi-arrow-down-short',
	flat: 'bi bi-dash',
};

const deltaColorClass = (good) => {
	if (good === true) return 'text-success';
	if (good === false) return 'text-danger';
	return 'text-muted';
};

/**
 * StatCard Component
 * A reusable card component for displaying statistics with an icon, title, and value
 *
 * @param {string} title - The title/label for the statistic
 * @param {string|number} value - The numeric value to display
 * @param {string} icon - Bootstrap icon class (e.g., "bi bi-buildings")
 * @param {string} className - Additional CSS classes (optional)
 * @param {number[]} spark - Optional sparkline y-values rendered under the value
 * @param {object} delta - Optional preformatted delta: { text, direction: 'up'|'down'|'flat', good }
 *                         good true=green, false=red, null/undefined=gray
 * @param {string} tooltip - Optional plain-English formula + good/bad direction; rendered
 *                           as an info icon with a title attribute
 *
 * The spark/delta/tooltip props are additive and default to undefined: when absent the
 * card renders byte-identical to its original markup, so existing consumers are unaffected.
 * Callers compute delta from payload data; StatCard renders, never computes.
 */
const StatCard = ({ title, value, icon, className = '', spark, delta, tooltip }) => {
	return (
		<div className={`stat-card ${className}`.trim()}>
			<div className="stat-card-title">
				{title.split('\n').map((line, index) => (
					<React.Fragment key={index}>
						{line}
						{index < title.split('\n').length - 1 && <br />}
					</React.Fragment>
				))}
				{tooltip ? (
					<i
						className="bi bi-info-circle ms-2 text-muted stat-card-tooltip"
						title={tooltip}
						role="img"
						aria-label={`About ${title}`}
					/>
				) : null}
			</div>
			<div className="stat-card-body">
				<div className="stat-card-icon">
					<i className={icon} />
				</div>
				<div className="stat-card-value">
					{value}
				</div>
			</div>
			{delta ? (
				<div className={`stat-card-delta small ${deltaColorClass(delta.good)}`}>
					<i className={DELTA_ICONS[delta.direction] || DELTA_ICONS.flat} aria-hidden="true" />{' '}
					{delta.text}
				</div>
			) : null}
			{Array.isArray(spark) ? (
				<div className="stat-card-spark mt-2">
					<Sparkline points={spark} />
				</div>
			) : null}
		</div>
	);
};

StatCard.propTypes = {
	title: PropTypes.string.isRequired,
	value: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number
	]).isRequired,
	icon: PropTypes.string.isRequired,
	className: PropTypes.string,
	spark: PropTypes.arrayOf(PropTypes.number),
	delta: PropTypes.shape({
		text: PropTypes.string.isRequired,
		direction: PropTypes.oneOf(['up', 'down', 'flat']).isRequired,
		good: PropTypes.bool,
	}),
	tooltip: PropTypes.string,
};

StatCard.defaultProps = {
	className: '',
	spark: undefined,
	delta: undefined,
	tooltip: undefined,
};

export default StatCard;
